import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Plus, Mail, Phone, MapPin, Wrench, Pencil, Trash2 } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";

export default function Vendors() {
  const { can } = useCam();
  const canWrite = can.admin || can.store;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vendor object or {} for new

  const load = async () => {
    setLoading(true);
    try { const r = await camApi.vendors(); setRows((r.vendors || []).filter((v) => v.is_active !== false)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor?")) return;
    try { await camApi.removeVendor(id); toast.success("Vendor removed"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Store className="h-4 w-4 text-cobalt-500" /><div className="overline">Vendors &amp; service partners</div></div>
        {canWrite && <Button size="sm" className="bg-cobalt-500 hover:bg-cobalt-600" onClick={() => setEditing({})}><Plus className="h-4 w-4 mr-1" />Add vendor</Button>}
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-12 text-center text-sm text-muted-foreground">Loading vendors…</div>
      ) : rows.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center max-w-md mx-auto">
          <Store className="h-9 w-9 mx-auto text-muted-foreground/40" />
          <h3 className="font-display text-lg tracking-tight mt-3">No vendors yet</h3>
          <p className="text-sm text-muted-foreground mt-1.5">Add the suppliers and service centres you buy from — then link them to assets and AMC contracts.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((v) => (
            <div key={v.id} className="glass rounded-2xl p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-base tracking-tight truncate">{v.name}</div>
                  {v.contact_person && <div className="text-xs text-muted-foreground truncate">{v.contact_person}</div>}
                </div>
                {canWrite && (
                  <div className="flex gap-0.5 shrink-0">
                    <button onClick={() => setEditing(v)} className="p-1 text-muted-foreground hover:text-cobalt-500"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(v.id)} className="p-1 text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                {v.email && <a href={`mailto:${v.email}`} className="flex items-center gap-2 text-muted-foreground hover:text-cobalt-500"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{v.email}</span></a>}
                {v.phone && <a href={`tel:${v.phone}`} className="flex items-center gap-2 text-muted-foreground hover:text-cobalt-500"><Phone className="h-3.5 w-3.5 shrink-0" />{v.phone}</a>}
                {v.service_center && <div className="flex items-center gap-2 text-muted-foreground"><Wrench className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{v.service_center}</span></div>}
                {v.address && <div className="flex items-start gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span className="line-clamp-2">{v.address}</span></div>}
              </div>
              {v.gstin && <div className="mt-3 text-[11px] text-mono text-muted-foreground">GSTIN · {v.gstin}</div>}
            </div>
          ))}
        </div>
      )}

      {editing && <VendorDialog vendor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VendorDialog({ vendor, onClose, onSaved }) {
  const isNew = !vendor.id;
  const [f, setF] = useState({
    name: vendor.name || "", contact_person: vendor.contact_person || "", email: vendor.email || "",
    phone: vendor.phone || "", gstin: vendor.gstin || "", service_center: vendor.service_center || "",
    address: vendor.address || "", notes: vendor.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) { toast.error("Vendor name is required."); return; }
    setBusy(true);
    try {
      if (isNew) { await camApi.createVendor(f); toast.success("Vendor added"); }
      else { await camApi.updateVendor(vendor.id, f); toast.success("Vendor updated"); }
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto touch-scroll">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">{isNew ? "Add vendor" : "Edit vendor"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs">Vendor name *</Label><Input className="mt-1" value={f.name} onChange={set("name")} placeholder="e.g. Dell India / ABC Computers" /></div>
          <div><Label className="text-xs">Contact person</Label><Input className="mt-1" value={f.contact_person} onChange={set("contact_person")} /></div>
          <div><Label className="text-xs">Phone</Label><Input className="mt-1" value={f.phone} onChange={set("phone")} /></div>
          <div><Label className="text-xs">Email</Label><Input className="mt-1" value={f.email} onChange={set("email")} /></div>
          <div><Label className="text-xs">GSTIN</Label><Input className="mt-1 text-mono text-xs" value={f.gstin} onChange={set("gstin")} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Service centre</Label><Input className="mt-1" value={f.service_center} onChange={set("service_center")} placeholder="Authorised service centre / branch" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Textarea className="mt-1" rows={2} value={f.address} onChange={set("address")} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Notes</Label><Textarea className="mt-1" rows={2} value={f.notes} onChange={set("notes")} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Saving…" : isNew ? "Add vendor" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
